import { addDays } from 'date-fns';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { Cart, CartItem, PaymentOption } from 'src/models/cart/_types';
import { denormalizeCart, normalizeCart } from 'src/models/cart/normalizers';
import { Group } from 'src/models/group/_types';
import { fetchGroup } from 'src/models/group/group';
import { filterGroupPaymentOptions } from 'src/models/group/utils';
import { fetchPackage } from 'src/models/package/package';
import { fetchProduct, fetchProductVariant } from 'src/models/product/product';
import {
  constrainQuantityToStock,
  getStockAvailabilityMessage,
} from 'src/models/product/utils';
import { fetchWorkoutPlan } from 'src/models/workout-plan/workout-plan';
import { Identifier } from 'src/types/utilityTypes';
import toast from 'src/utils/toast';

const CART_STORAGE_KEY = 'excom-cart';

interface CartContextType {
  cart: Cart | null;
  addToCart: (
    item: any,
    options?: {
      type?: string;
      quantity?: number;
      paymentOption?: PaymentOption;
      metadata?: Record<string, any>;
    }
  ) => Promise<void>;
  removeFromCart: (cartItemIndex: number) => void;
  updateQuantity: (cartItemIndex: number, quantity: number) => Promise<void>;
  updateLocationId: (locationId: Identifier | undefined) => void;
  clearCart: () => void;
  getCartItem: (itemId: Identifier, itemType: string) => CartItem | undefined;
  isInCart: (itemId: Identifier, itemType: string) => boolean;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: React.ReactNode;
  userId?: Identifier;
}

export const CartProvider: React.FC<CartProviderProps> = ({
  children,
  userId: propUserId,
}) => {
  const { session, user } = useOptionalSession();
  const userId = propUserId || user?.id;

  const [cart, setCart] = useState<Cart | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Helper function to get item details based on type
  const fetchItemDetails = useCallback(
    async (itemId: Identifier, itemType: string) => {
      switch (itemType) {
        case 'Group':
          return await fetchGroup({ id: itemId });
        case 'Fbm::Package':
          return await fetchPackage({ id: itemId });
        case 'Exercisecom::Product':
          return await fetchProduct({ id: itemId });
        case 'ProductVariant':
          return await fetchProductVariant({ id: itemId });
        case 'WorkoutPlan':
          return await fetchWorkoutPlan({ id: itemId });
        default:
          throw new Error(`Unsupported item type: ${itemType}`);
      }
    },
    []
  );

  const isValidCart = useCallback(
    (cart: any, userId?: Identifier): cart is Cart => {
      try {
        // Basic structure validation
        if (!cart || typeof cart !== 'object') return false;
        if (!Array.isArray(cart.cartItems)) return false;

        // User validation - cart should belong to current user
        if (userId && cart.userId !== userId) return false;

        // Validate cart items structure
        for (const item of cart.cartItems) {
          if (!item || typeof item !== 'object') return false;
          if (typeof item.quantity !== 'number' || item.quantity <= 0)
            return false;
          if (!item.itemId || !item.itemType) return false;

          // Validate payment option exists
          if (!item.paymentOption || typeof item.paymentOption !== 'object')
            return false;
          if (typeof item.paymentOption.amount !== 'number') return false;
        }

        return true;
      } catch (error) {
        return false;
      }
    },
    []
  );

  // Helper functions for localStorage operations
  const saveCartToLocalStorage = useCallback((cart: Cart | null) => {
    try {
      if (cart) {
        localStorage.setItem(
          CART_STORAGE_KEY,
          JSON.stringify({
            expires: addDays(new Date(), 1).toISOString(),
            cart: denormalizeCart(cart),
          })
        );
      } else {
        localStorage.removeItem(CART_STORAGE_KEY);
      }
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }, []);

  const loadCartFromLocalStorage = useCallback(
    (userId?: Identifier): Cart | null => {
      try {
        const savedCart = localStorage.getItem(CART_STORAGE_KEY);
        if (!savedCart) return null;

        const parsedCart = normalizeCart(JSON.parse(savedCart).cart || {});

        // Validate cart structure and compatibility
        if (!isValidCart(parsedCart, userId)) {
          localStorage.removeItem(CART_STORAGE_KEY);
          return null;
        }

        if (parsedCart.cartItems && parsedCart.cartItems.length > 0) {
          parsedCart.cartItems.forEach(async (item) => {
            if (!item.item && item.itemId && item.itemType) {
              const itemDetails = await fetchItemDetails(
                item.itemId as Identifier,
                item.itemType as string
              );
              item.item = itemDetails as any;
            }
          });
        }

        return parsedCart;
      } catch (error) {
        localStorage.removeItem(CART_STORAGE_KEY);
        return null;
      }
    },
    [fetchItemDetails, isValidCart]
  );

  // Initialize cart when user changes
  useEffect(() => {
    if (!isInitialized) {
      // Try to load cart from localStorage first
      const savedCart = loadCartFromLocalStorage(userId);

      if (savedCart) {
        setCart(savedCart);
      } else {
        // Create fresh cart if no valid saved cart
        setCart({
          userId: userId || '',
          cartItems: [],
        });
      }

      setIsInitialized(true);
    }
  }, [userId, isInitialized, loadCartFromLocalStorage]);

  // Save cart to localStorage whenever cart changes
  useEffect(() => {
    if (isInitialized && cart) {
      saveCartToLocalStorage(cart);
    }
  }, [cart, isInitialized, saveCartToLocalStorage]);

  // Add item to cart
  const addToCart = useCallback(
    async (
      item: any,
      options: {
        type?: string;
        quantity?: number;
        paymentOption?: PaymentOption;
        metadata?: Record<string, any>;
      } = {}
    ) => {
      const { type = 'product-variant', quantity = 1, paymentOption, metadata } = options;

      const cartItemUserId = (metadata?.userId as Identifier | undefined) ?? userId;

      // Map frontend types to backend types
      let itemType: string;
      let itemId: Identifier;

      if (type === 'product-variant' && item.id) {
        itemType = 'ProductVariant';
        itemId = item.id;
      } else if (type === 'product' && item.id) {
        itemType = 'Exercisecom::Product';
        itemId = item.id;
      } else if (type === 'group' && item.id) {
        itemType = 'Group';
        itemId = item.id;
      } else if (type === 'package' && item.id) {
        itemType = 'Fbm::Package';
        itemId = item.id;
      } else if (type === 'plan' && item.id) {
        itemType = 'WorkoutPlan';
        itemId = item.id;
      } else {
        toast.error('Invalid item type or missing ID');
        return;
      }

      try {
        // Fetch item details if needed
        let itemDetails = item;
        if (!item?.paymentOptions || itemType === 'ProductVariant') {
          itemDetails = await fetchItemDetails(itemId, itemType);
        }

        if (itemType === 'Group' && itemDetails.paymentOptions?.length) {
          const filteredOptions = filterGroupPaymentOptions(
            itemDetails as Group,
            itemDetails.paymentOptions
          );
          itemDetails = { ...itemDetails, paymentOptions: filteredOptions };
        }

        // Create default payment option if none exist
        let selectedPaymentOption = paymentOption;
        if (!selectedPaymentOption && itemDetails.paymentOptions?.length > 0) {
          selectedPaymentOption = itemDetails.paymentOptions[0];
        } else if (!selectedPaymentOption) {
          selectedPaymentOption = {
            type: 'purchase',
            amount: itemDetails.price || 0,
            currency: cart?.currency || session?.defaultCurrency || 'usd',
            name: 'One Time Purchase',
            location: null,
          };
        }

        const newCartItem: CartItem = {
          quantity,
          userId: cartItemUserId,
          itemMeta: metadata,
          item: {
            ...itemDetails,
            paymentOptions: itemDetails.paymentOptions || [
              selectedPaymentOption,
            ],
            paymentOption: selectedPaymentOption,
            imageUrl: itemDetails.imageUrl || '',
            name: itemDetails.name || itemDetails.displayName || '',
          },
          name: itemDetails.name || itemDetails.displayName || '',
          itemId,
          itemType,
          paymentOption: selectedPaymentOption,
        };

        const currentCart = cart || { userId, cartItems: [] };

        // Check if item already exists
        const existingItemIndex = currentCart.cartItems.findIndex(
          (cartItem) =>
            cartItem.itemId === itemId &&
            cartItem.itemType === itemType &&
            JSON.stringify(cartItem.paymentOption) ===
              JSON.stringify(selectedPaymentOption)
        );

        let updatedCartItems;
        if (existingItemIndex >= 0) {
          // Update existing item quantity
          updatedCartItems = [...currentCart.cartItems];
          updatedCartItems[existingItemIndex] = {
            ...updatedCartItems[existingItemIndex],
            quantity: updatedCartItems[existingItemIndex].quantity + quantity,
          };
        } else {
          // Add new item
          updatedCartItems = [...currentCart.cartItems, newCartItem];
        }

        const updatedCart = {
          ...currentCart,
          cartItems: updatedCartItems,
        };

        setCart(updatedCart as Cart);
        toast.success('Item added to cart successfully!');
      } catch (error: any) {
        toast.error(error?.message || 'Failed to add item to cart');
      }
    },
    [userId, cart, fetchItemDetails, session?.defaultCurrency]
  );

  // Remove item from cart
  const removeFromCart = useCallback(
    (cartItemIndex: number) => {
      if (!cart) return;

      const updatedCartItems = cart.cartItems.filter(
        (_, index) => index !== cartItemIndex
      );
      const updatedCart = { ...cart, cartItems: updatedCartItems };
      setCart(updatedCart);
    },
    [cart]
  );

  // Update item quantity
  const updateQuantity = useCallback(
    async (cartItemIndex: number, quantity: number) => {
      if (!cart) return;

      if (quantity <= 0) {
        removeFromCart(cartItemIndex);
        return;
      }

      const cartItem = cart.cartItems[cartItemIndex];
      let constrainedQuantity = quantity;

      // For ProductVariant items, validate against available stock
      if (cartItem.itemType === 'ProductVariant' && cartItem.itemId) {
        try {
          // Fetch the product variant to get current stock levels
          const variant = await fetchProductVariant({ id: cartItem.itemId });

          // Constrain quantity to available stock
          constrainedQuantity = constrainQuantityToStock(
            variant,
            quantity,
            cart.locationId,
            cart,
            cartItemIndex
          );

          // Show warning if quantity was reduced due to stock limitations
          if (constrainedQuantity < quantity) {
            const stockMessage = getStockAvailabilityMessage(
              variant,
              cart.locationId,
              cart,
              cartItemIndex
            );

            const message = stockMessage
              ? `Quantity reduced to ${constrainedQuantity}. ${stockMessage}`
              : `Quantity reduced to ${constrainedQuantity} due to stock limitations.`;

            toast.error(message);
          }
        } catch (error) {
          toast.error(`Failed to validate stock for product variant: ${error}`);
        }
      }

      const updatedCartItems = [...cart.cartItems];
      updatedCartItems[cartItemIndex] = {
        ...updatedCartItems[cartItemIndex],
        quantity: constrainedQuantity,
      };

      const updatedCart = { ...cart, cartItems: updatedCartItems };
      setCart(updatedCart);
    },
    [cart, removeFromCart]
  );

  // Clear entire cart
  const clearCart = useCallback(() => {
    setCart((prevCart) => {
      if (!prevCart) return prevCart;
      return { ...prevCart, cartItems: [] };
    });
  }, []);

  // Update cart location
  const updateLocationId = useCallback(
    (locationId: Identifier | undefined) => {
      setCart((prevCart) => {
        if (!prevCart) return prevCart;
        return { ...prevCart, locationId };
      });
    },
    []
  );

  // Get specific cart item
  const getCartItem = useCallback(
    (itemId: Identifier, itemType: string) => {
      return cart?.cartItems.find(
        (item) => item.itemId === itemId && item.itemType === itemType
      );
    },
    [cart]
  );

  // Check if item is in cart
  const isInCart = useCallback(
    (itemId: Identifier, itemType: string) => {
      return (
        cart?.cartItems.some(
          (item) => item.itemId === itemId && item.itemType === itemType
        ) || false
      );
    },
    [cart]
  );

  // Calculate cart totals
  const { totalItems, totalPrice } = useMemo(() => {
    if (!cart?.cartItems) {
      return { totalItems: 0, totalPrice: 0 };
    }

    const items = cart.cartItems.reduce(
      (total, item) => total + item.quantity,
      0
    );
    const price = cart.cartItems.reduce(
      (total, item) =>
        total + (item.paymentOption?.amount || 0) * item.quantity,
      0
    );

    return { totalItems: items, totalPrice: price };
  }, [cart]);

  const value = useMemo(
    () => ({
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      updateLocationId,
      clearCart,
      getCartItem,
      isInCart,
      totalItems,
      totalPrice,
    }),
    [
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      updateLocationId,
      clearCart,
      getCartItem,
      isInCart,
      totalItems,
      totalPrice,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Export types for external use
export type { Cart, CartItem, PaymentOption } from 'src/models/cart/_types';
